/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { Options } from '../types';

import {
  DEFAULT_BUILD_CONTAINER_MEMORY_LIMIT,
  DEFAULT_ENVIRONMENTS,
  DEFAULT_MEMORY_LIMIT,
} from './const';

export interface InvalidEnvironmentError {
  readonly message: string;
}

export class InvalidEnvironmentNameError implements InvalidEnvironmentError {
  readonly message: string;

  constructor() {
    this.message = `Environment name and order needs to be defined in your deployment settings.`;
  }
}

export class InvalidEnvironmentOrderError implements InvalidEnvironmentError {
  readonly message: string;

  constructor() {
    this.message = `Environment types must be in the order: test, staging, then production, across all of your steps.`;
  }
}

export class DuplicateEnvironmentError implements InvalidEnvironmentError {
  readonly message: string;

  constructor(public readonly value: string) {
    this.message = `Environment name "${value}" occurs multiple times in the pipeline.`;
  }
}

/**
 * Holds all information needed by the validator.
 */
export class ParsingState {
  private nextEnvironmentGroupAllowed = 0;
  private JSObject: any;
  private visitedEnvironments = [] as any;
  private environments = [] as any;
  private customPipelines = false;

  constructor(options?: Options) {
    this.environments =
      options &&
      options.environments &&
      [].concat(...(options.environments || [])).length
        ? options.environments
        : DEFAULT_ENVIRONMENTS;
  }

  // eslint-disable-next-line consistent-return
  visitEnvironment(
    value: string
  ):
    | InvalidEnvironmentNameError
    | DuplicateEnvironmentError
    | InvalidEnvironmentOrderError
    | void {
    const environmentGroupIndex = this.environments.findIndex(
      (group: any) => group.indexOf(value) !== -1
    );
    const isDefinedEnvironment = environmentGroupIndex !== -1;
    const isVisitedEnvironment = this.visitedEnvironments.indexOf(value) !== -1;

    if (!isDefinedEnvironment) {
      return new InvalidEnvironmentNameError();
    }
    if (isVisitedEnvironment) {
      return new DuplicateEnvironmentError(value);
    }
    this.visitedEnvironments.push(value);

    if (environmentGroupIndex < this.nextEnvironmentGroupAllowed) {
      return new InvalidEnvironmentOrderError();
    }
    this.nextEnvironmentGroupAllowed = environmentGroupIndex;
  }

  setCustomPipelines(customStep: any): void {
    this.customPipelines = customStep;
  }

  hasCustomPipelines(): boolean {
    return this.customPipelines;
  }

  resetEnvironments(): void {
    this.nextEnvironmentGroupAllowed = 0;
    this.visitedEnvironments = [];
  }

  setJSObject(JSObject: any): void {
    this.JSObject = JSObject;
  }

  getDefinitions() {
    return this.JSObject.definitions;
  }

  getOptions() {
    return this.JSObject.options;
  }

  getPipelines() {
    return this.JSObject.pipelines;
  }

  getStages() {
    const stages = [];
    // add parallel stages
    stages.push(
      ...(this.JSObject.pipelines?.default
        ?.filter((p: any) => p?.parallel)
        .map((p: any) => p?.parallel)
        .flat()
        .map((p: any) => p?.stage) || [])
    );
    // add normal stages
    stages.push(
      ...(this.JSObject.pipelines?.default?.map((p: any) => p?.stage) || [])
    );
    return stages.filter(s => s);
  }

  getCachesDefinitions(): string[] {
    let custom: string[] = [];

    const defaults = [
      'maven',
      'gradle',
      'node',
      'pip',
      'composer',
      'dotnetcore',
      'sbt',
      'ivy2',
      'docker',
    ];

    const definitions = this.getDefinitions();

    if (definitions && definitions.caches) {
      custom = Object.keys(definitions.caches);
    }

    return custom.concat(defaults);
  }

  getServicesDefinitions(): any {
    const allDefinitions = { docker: { memory: DEFAULT_MEMORY_LIMIT } } as any;
    const definitions = this.getDefinitions();

    if (definitions && definitions.services) {
      for (const key in definitions.services) {
        // overwrite defaults
        if (key) {
          allDefinitions[key] = definitions.services[key];
        }
      }
    }

    return allDefinitions;
  }

  getGlobalOptionsSize(): string | undefined {
    const options = this.getOptions();
    return options ? options.size : undefined;
  }

  getGlobalOptionsDocker(): boolean | undefined {
    const options = this.getOptions();
    return options ? options.docker : undefined;
  }

  getSumOfServicesMemory(services: string[]): number {
    let totalMemory = 0;
    services.forEach(service => {
      const servicesDefinitions = this.getServicesDefinitions();
      if (servicesDefinitions && servicesDefinitions[service]) {
        const { memory } = servicesDefinitions[service];
        totalMemory += memory || DEFAULT_MEMORY_LIMIT;
      }
    });
    return totalMemory;
  }

  convertToSize(sizex?: string): number {
    return sizex === '2x' ? 2 : 1;
  }

  getBuildContainerMemoryLimit(stepSize?: string): number {
    return (
      this.convertToSize(stepSize || this.getGlobalOptionsSize()) *
      DEFAULT_BUILD_CONTAINER_MEMORY_LIMIT
    );
  }
}
